/**
 * Results summary — three columns inside one rounded card: latest financial
 * results (document links), company updates, and the next upcoming event.
 * Mirrors the `.results / .res-3` block in the mock.
 */

import AgntTextIcon from 'agnt/components/agnt-text-icon';

import styles from './results-summary.module.scss';

import type { ResultsSummary as ResultsSummaryContent } from '../content';

// Internal hrefs (starting with `/`) stay in the same tab; external open in a new tab.
// Used for the plain document chips (HTML/PDF) and the pill CTAs — the arrow
// "view …" links use `AgntTextIcon`, which handles this itself.
function linkProps(href: string) {
  return href.startsWith('/') ? {} : { target: '_blank' as const, rel: 'noopener noreferrer' };
}

export default function ResultsSummary({ content }: { content: ResultsSummaryContent }) {
  const { latestResults, companyUpdates, upcomingEvents } = content;

  return (
    <div className={styles.results}>
      <div className={styles.grid}>
        {/* Column 1 — latest financial results */}
        <div className={styles.col} data-reveal>
          <h3 className={styles.colHeading}>
            {latestResults.kicker}
            <b>{latestResults.title}</b>
          </h3>
          <hr className={styles.rule} />
          <div className={styles.quarter}>{latestResults.quarter}</div>
          <div className={styles.quarterDetail}>{latestResults.quarterDetail}</div>
          {latestResults.rows.map(row => (
            <div key={row.label} className={styles.docRow}>
              <span>{row.label}</span>
              <div className={styles.docLinks}>
                {row.links.map(link => (
                  <a key={`${row.label}-${link.label}`} href={link.href} {...linkProps(link.href)}>
                    {link.label}
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Column 2 — company updates */}
        <div className={styles.col} data-reveal>
          <h3 className={styles.colHeading}>
            {companyUpdates.kicker}
            <b>{companyUpdates.title}</b>
          </h3>
          <hr className={styles.rule} />
          <div className={styles.updateHighlight}>{companyUpdates.highlight}</div>
          <div className={styles.linkStack}>
            <AgntTextIcon href={companyUpdates.primary.href} label={companyUpdates.primary.label} tone="navy" />
            <AgntTextIcon href={companyUpdates.secondary.href} label={companyUpdates.secondary.label} />
          </div>
        </div>

        {/* Column 3 — upcoming events */}
        <div className={styles.col} data-reveal>
          <h3 className={styles.colHeading}>
            {upcomingEvents.kicker}
            <b>{upcomingEvents.title}</b>
          </h3>
          <hr className={styles.rule} />
          {upcomingEvents.when && upcomingEvents.eventTitle && (
            <>
              <div className={styles.eventDate}>{upcomingEvents.when}</div>
              <div className={styles.eventTitle}>{upcomingEvents.eventTitle}</div>
              <div className={styles.eventLinks}>
                {(upcomingEvents.eventLinks ?? []).map(link => (
                  <AgntTextIcon key={link.label} href={link.href} label={link.label} tone="navy" />
                ))}
              </div>
            </>
          )}
          <AgntTextIcon href={upcomingEvents.viewAll.href} label={upcomingEvents.viewAll.label} />
          <div className={styles.ctaRow}>
            {upcomingEvents.ctas.map(cta => (
              <a key={cta.label} className={styles.ctaPill} href={cta.href} {...linkProps(cta.href)}>
                {cta.label}
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
